import { useCallback, useMemo, useState } from 'react';

import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/20/solid';

import { CanceledGroupCard } from './components/CanceledGroupCard';
import { GroupCard } from './components/GroupCard';
import type { GroupConfirmKind } from './components/groupConfirmCopy';

import type { GroupSubscriptionsElementProps } from './GroupSubscriptionsElement.types';

// A canceled group subscription becomes "stale" 365 days after `ends_at`
// Stale rows move into the collapsed archive section
const ARCHIVE_STALENESS_DAYS = 365;
const STALE_THRESHOLD_MS = ARCHIVE_STALENESS_DAYS * 24 * 60 * 60 * 1000;

export function GroupSubscriptionsElement(props: GroupSubscriptionsElementProps) {
  const {
    element,
    translations,
    onOpenManageSeats,
    onOpenInviteByEmail,
    onOpenBulkInviteCsv,
    onOpenConfirm,
    onViewBilling,
    onCancelSubscription,
    onRestoreSubscription,
    renderCard,
    onRetryFailedPayment,
    updatePaymentMethodAction,
  } = props;

  const groups = element.attrs?.data?.groups ?? [];

  const { activeGroups, freshCanceledGroups, staleCanceledGroups } = useMemo(() => {
    const active: typeof groups = [];
    const freshCanceled: typeof groups = [];
    const staleCanceled: typeof groups = [];
    const now = Date.now();
    for (const g of groups) {
      if (g.status !== 'canceled') {
        active.push(g);
        continue;
      }
      const endedMs = g.ends_at ? new Date(g.ends_at).getTime() : null;
      const isStale = endedMs != null && now - endedMs > STALE_THRESHOLD_MS;
      (isStale ? staleCanceled : freshCanceled).push(g);
    }
    return { activeGroups: active, freshCanceledGroups: freshCanceled, staleCanceledGroups: staleCanceled };
  }, [groups]);

  // Single-expansion accordion: opening one card closes the others. Tracked
  // here so the closed-card → open-card transition stays inside the element.
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(() => activeGroups[0]?.id ?? null);
  const [archiveOpen, setArchiveOpen] = useState(false);

  const expandedGroupId = useMemo(() => {
    if (selectedGroupId === null) return null;
    if (activeGroups.some((g) => g.id === selectedGroupId)) return selectedGroupId;
    return activeGroups[0]?.id ?? null;
  }, [selectedGroupId, activeGroups]);

  const handleToggleExpanded = useCallback(
    (groupId: string) => setSelectedGroupId((prev) => (prev === groupId ? null : groupId)),
    [],
  );

  if (groups.length === 0) {
    const emptyTitle =
      translations?.['group_subscriptions.empty_state_title'] ??
      translations?.['group_subscriptions.empty_state'] ??
      'No group subscriptions yet';
    const emptyDescription =
      translations?.['group_subscriptions.empty_state_description'] ??
      "Once your group plan is active, it'll show up here so you can invite members and manage seats.";

    return (
      <div className="flex w-full flex-col items-center justify-center gap-2 rounded-md border border-dashed border-gray-200 px-4 py-12 text-center">
        <p className="text-base font-medium text-gray-700">{emptyTitle}</p>
        <p className="max-w-sm text-sm text-gray-500">{emptyDescription}</p>
      </div>
    );
  }

  const archiveLabelTemplate =
    translations?.['group_subscriptions.archive.toggle'] ?? 'Archived subscriptions ({{count}})';
  const archiveLabel = archiveLabelTemplate.replace('{{count}}', String(staleCanceledGroups.length));

  return (
    <div className="flex flex-col w-full gap-4">
      {activeGroups.map((group) => {
        const expandedProps = {
          isExpanded: expandedGroupId === group.id,
          onToggleExpanded: () => handleToggleExpanded(group.id),
        };
        if (renderCard) return <div key={group.id}>{renderCard(group, expandedProps)}</div>;
        return (
          <GroupCard
            key={group.id}
            group={group}
            isExpanded={expandedProps.isExpanded}
            onToggleExpanded={expandedProps.onToggleExpanded}
            onOpenManageSeats={onOpenManageSeats ? () => onOpenManageSeats(group.id) : undefined}
            onOpenInviteByEmail={onOpenInviteByEmail ? () => onOpenInviteByEmail(group.id) : undefined}
            onOpenBulkInviteCsv={onOpenBulkInviteCsv ? () => onOpenBulkInviteCsv(group.id) : undefined}
            onOpenConfirm={
              onOpenConfirm
                ? (kind: GroupConfirmKind, seatIds: string[]) => onOpenConfirm(group.id, kind, seatIds)
                : undefined
            }
            onViewBilling={onViewBilling ? () => onViewBilling(group.id) : undefined}
            onCancelSubscription={onCancelSubscription ? () => onCancelSubscription(group.id) : undefined}
            onRestoreSubscription={onRestoreSubscription ? () => onRestoreSubscription(group.id) : undefined}
            onRetryFailedPayment={onRetryFailedPayment ? () => onRetryFailedPayment(group.id) : undefined}
            updatePaymentMethodAction={updatePaymentMethodAction}
            translations={translations}
          />
        );
      })}

      {freshCanceledGroups.map((group) => (
        <CanceledGroupCard key={group.id} group={group} translations={translations} />
      ))}

      {staleCanceledGroups.length > 0 && (
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => setArchiveOpen((prev) => !prev)}
            aria-expanded={archiveOpen}
            aria-controls="group-subscriptions-archive"
            className="flex items-center gap-1 self-start rounded text-sm font-medium text-gray-700 hover:text-gray-900"
          >
            {archiveOpen ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
            <span>{archiveLabel}</span>
          </button>
          {/* Always rendered (hidden when collapsed) so `aria-controls` keeps pointing
              at a real DOM node — assistive tech needs the target to exist either way. */}
          <div id="group-subscriptions-archive" hidden={!archiveOpen} className="flex flex-col gap-2">
            {staleCanceledGroups.map((group) => (
              <CanceledGroupCard key={group.id} group={group} translations={translations} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
